import {
  InstrumentType,
  ProductType,
  TransactionType,
  TransactionTypeFacade,
  ValidityType,
} from 'v3/common/types/type';
import { calculate } from './calculate';
import {
  BrokerageConfig,
  BrokerageFeeDetailsProps,
  BrokerageFees,
  Charge,
  ChargeConfig,
  ChargeSides,
} from './type';
import { ParseOrderBrokerage } from 'v3/common/types/order';
import { getLowerPrice, roundToTwoDecimal } from './helper';

const getSttCharge = (buyValue: number, sellValue: number, productType: ProductType = 'MIS'): number => {
  if (productType === 'CNC' || productType === 'NRML') return buyValue + sellValue;
  return sellValue;
};

export const getOrderWithBrokerage = (
  orderDetail: ParseOrderBrokerage,
  slippage: number = 0,
  brokerageCharge: BrokerageConfig
) => {
  const transactionCharge = brokerageFees({ orderDetail, slippage, brokerageCharge });
  return {
    ...orderDetail,
    transactionCharge,
  };
};

export const getPositionWithBrokerage = (entryOrder: ParseOrderBrokerage, exitOrder: ParseOrderBrokerage) => {
  console.log('Calculating Position Brokerage', { entryOrder, exitOrder });
  if (!entryOrder.transactionCharge || !exitOrder.transactionCharge) return null;

  return {
    brokerageAmount: entryOrder.transactionCharge.brokerageRate + exitOrder.transactionCharge.brokerageRate,
    stt: entryOrder.transactionCharge.stt + exitOrder.transactionCharge.stt,
    exchangeTransactionCharges:
      entryOrder.transactionCharge.exchangeTransactionCharges +
      exitOrder.transactionCharge.exchangeTransactionCharges,
    sebi: entryOrder.transactionCharge.sebi + exitOrder.transactionCharge.sebi,
    gst: entryOrder.transactionCharge.gst + exitOrder.transactionCharge.gst,
    nseipft: entryOrder.transactionCharge.nseipft + exitOrder.transactionCharge.nseipft,
    stampDuty: entryOrder.transactionCharge.stampDuty + exitOrder.transactionCharge.stampDuty,
    totalBrokerageFees: entryOrder.transactionCharge.total + exitOrder.transactionCharge.total,
  };
};

const getBuyAndSellValues = (
  type: TransactionType,
  isEntry: boolean,
  entryPrice: number,
  exitPrice: number,
  quantity: number
): { buyValue: number; sellValue: number } => {
  if (type === 'buy') {
    return {
      buyValue: isEntry ? entryPrice * quantity : exitPrice * quantity,
      sellValue: 0,
    };
  } else if (type === 'sell') {
    return {
      buyValue: 0,
      sellValue: isEntry ? entryPrice * quantity : exitPrice * quantity,
    };
  }

  return { buyValue: 0, sellValue: 0 };
};

const brokerageFees = ({ orderDetail, brokerageCharge }: BrokerageFeeDetailsProps): BrokerageFees => {
  const isEntry = orderDetail.is_entry === 'true';
  const { quantity, entryPrice, exitPrice, segment, transaction_type } = orderDetail;
  const transactionType: TransactionTypeFacade = transaction_type.toUpperCase() as TransactionTypeFacade;
  const { buyValue, sellValue } = getBuyAndSellValues(
    transaction_type,
    isEntry,
    Number(entryPrice),
    Number(exitPrice),
    Number(quantity)
  );

  //Computation of brokerage and other charges
  const brokerageRate = perOrderBrokerageCharge(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'brokerage') as Charge,
    buyValue,
    sellValue
  );

  const computeStt = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'stt') as Charge
  );
  const computeExchangeTransactionCharges = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'exchangeTransaction') as Charge
  );
  const computeSebi = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'sebi') as Charge
  );
  const computeGst = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'gst') as Charge
  );
  const computeNseipft = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'nseipft') as Charge
  );
  const computeStampDuty = getAmountFromValueOrPercentage(
    getCharge(brokerageCharge, segment, 'DAY', transactionType, 'stampDuty') as Charge
  );

  // calculate charges
  const stt = calculate.stt(computeStt, getSttCharge(buyValue, sellValue));
  const exchangeTransactionCharges = calculate.exchangeTransactionCharges(
    computeExchangeTransactionCharges,
    buyValue,
    sellValue
  );
  const sebi = calculate.sebi(computeSebi, buyValue, sellValue);
  const gst = calculate.gst(computeGst, brokerageRate, exchangeTransactionCharges, sebi);
  const nseipft = calculate.nseipft(computeNseipft, buyValue, sellValue);
  const stampDuty = calculate.stampDuty(computeStampDuty, buyValue);

  // total charges
  const total = roundToTwoDecimal(
    brokerageRate + stt + exchangeTransactionCharges + sebi + gst + nseipft + stampDuty
  );
  const breakEven = roundToTwoDecimal(total / Number(quantity));
  const netPNL = roundToTwoDecimal(total + breakEven);
  const entryPriceAfterBrokerage = transaction_type === 'buy' ? buyValue + netPNL : sellValue - netPNL;
  const exitPriceAfterBrokerage = transaction_type === 'sell' ? sellValue - netPNL : buyValue + netPNL;

  return {
    brokerageRate,
    stt,
    exchangeTransactionCharges,
    sebi,
    gst,
    nseipft,
    stampDuty,
    total,
    breakEven,
    brokerageBuyValue: buyValue,
    brokerageSellValue: sellValue,
    netPNL,
    entryPriceAfterBrokerage,
    exitPriceAfterBrokerage,
  };
};

const perOrderBrokerageCharge = (
  fees: { price: number; isPercentage: boolean; maxCap?: number } | null | undefined,
  entryPrice: number,
  exitPrice: number
): number => {
  if (!fees) return 0;
  if (entryPrice) return calculateFee(entryPrice, fees);
  if (exitPrice) return calculateFee(exitPrice, fees);
  return 0;
};

const calculateFee = (
  tradePrice: number,
  fees: { price: number; isPercentage: boolean; maxCap?: number }
): number => {
  if (!tradePrice) return 0;
  if (fees.isPercentage) {
    const calculatedFee = (fees.price / 100) * tradePrice;
    if (fees.maxCap !== undefined) {
      return getLowerPrice(calculatedFee, fees.maxCap);
    }
    return calculatedFee;
  }
  return fees.price;
};

const getAmountFromValueOrPercentage = (
  valueOrPercentage: { price: number; isPercentage: boolean } | null
): number => {
  if (!valueOrPercentage) return 0;
  return valueOrPercentage.price;
};

const isDirectObject = (obj: ChargeConfig) =>
  obj &&
  typeof obj === 'object' &&
  'price' in obj &&
  (obj as Charge).price !== undefined &&
  !('BUY' in obj) &&
  !('SELL' in obj) &&
  !('ALL' in obj);

export const getCharge = (
  brokerageConfig: BrokerageConfig,
  segment: InstrumentType,
  productType: ValidityType,
  transactionType: TransactionTypeFacade,
  chargeType: 'brokerage' | 'exchangeTransaction' | 'stampDuty' | 'gst' | 'sebi' | 'stt' | 'nseipft'
) => {
  const productConfig =
    brokerageConfig?.[chargeType]?.[segment]?.[productType] ?? brokerageConfig?.[chargeType]?.[segment]?.ALL;

  if (!productConfig) return null;

  // Case 1: Direct price object
  if (isDirectObject(productConfig)) return productConfig;

  // Case 2: BUY or SELL exists
  if (typeof productConfig === 'object' && (productConfig as ChargeSides)[transactionType] !== undefined) {
    return (productConfig as ChargeSides)[transactionType];
  }

  // Case 3: Fallback to ALL (when BUY/SELL missing)
  if (typeof productConfig === 'object' && 'ALL' in productConfig) {
    return (productConfig as ChargeSides).ALL;
  }
  return null;
};
